/**
 * ChangeAcceptedEmailDialog — move an ACCEPTED user's sign-in address.
 *
 * Miniplan S4 (`ai-docs/accepted-user-email-change-miniplan.md`), over
 * lexserver's `POST /api/org/:orgId/users/:userId/email/change` (S3).
 * PLATFORM_ADMIN only (D-2) — the trigger is rendered from LexPortal's own
 * `renderUserActions`, so the capability never enters the `org-admin` bundle
 * that LexIDE ships to org admins.
 *
 * This is NOT the pending-invitation correction. That one lives in org-admin's
 * `EditUserDialog` and is deliberately locked once a user accepts; this is the
 * separate path, and it does not relax that lock.
 *
 * **The one thing to understand before reading the JSX:** with `admin-verified`
 * the new address is marked verified by admin fiat — no proof of ownership —
 * so it becomes a WORKING LOGIN the moment this succeeds. A typo in the
 * destination therefore does not fail; it succeeds, pointed at a stranger.
 * That is why the confirm field retypes the NEW address rather than some
 * stable identifier for the target: the destination is the dangerous input.
 *
 * Gate + outcome logic is pure and unit-tested in `lib/change-user-email.ts`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { AtSign, Loader2, ShieldAlert, TriangleAlert } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import { PlatformApiError } from "@/lib/platform-api";
import {
  REASON_MAX,
  adviseOnFailure,
  classifyEmailChangeResult,
  describeOutcome,
  evaluateChangeEmailGate,
  isModeImplemented,
  type ChangeEmailTarget,
  type OwnershipMode,
} from "@/lib/change-user-email";

export interface ChangeAcceptedEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  /** Non-null while open. */
  user: ChangeEmailTarget | null;
  /** Called after a VERIFIED change — host reloads the list. */
  onChanged: (result: { userId: string; email: string; previousEmail?: string }) => void;
}

export function ChangeAcceptedEmailDialog({
  open,
  onOpenChange,
  orgId,
  user,
  onChanged,
}: ChangeAcceptedEmailDialogProps) {
  const api = usePlatformApi();

  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [mode, setMode] = useState<OwnershipMode | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ text: string; code: string | null } | null>(null);

  const userId = user?.userId ?? null;

  // Same guards as RenameOrgDialog, for the same reasons: hosts build this
  // prop fresh per render, so an object-identity dep would wipe typed input on
  // any unrelated parent render; and a slow response must never report against
  // whoever the dialog is pointed at by the time it lands.
  const runRef = useRef(0);

  useEffect(() => {
    if (!open || !userId) return;
    runRef.current += 1;
    setNewEmail("");
    setConfirmEmail("");
    // D-1: never selectable by omission — the operator must choose actively.
    setMode(null);
    setReason("");
    setError(null);
    setBusy(false);
  }, [open, userId]);

  const gate = user
    ? evaluateChangeEmailGate({
        currentEmail: user.email,
        newEmail,
        confirmEmail,
        mode,
        reason,
        busy,
      })
    : ({ canSubmit: false, reason: "No user selected." } as const);

  const handleSubmit = useCallback(async () => {
    if (!user || !gate.canSubmit || !mode) return;
    const run = runRef.current;
    const target = user.userId;
    setBusy(true);
    setError(null);
    try {
      const res = await api.changeAcceptedUserEmail(orgId, target, {
        newEmail: gate.newEmail,
        ownershipMode: mode,
        reason: reason.trim() || undefined,
      });
      if (runRef.current !== run) return;

      // Never trust the 200 — see classifyEmailChangeResult.
      const outcome = classifyEmailChangeResult(gate.newEmail, res.data);
      const problem = describeOutcome(outcome);
      if (problem) {
        setError({ text: problem, code: null });
        return;
      }

      onChanged({
        userId: target,
        email: gate.newEmail,
        previousEmail: outcome.kind === "applied" ? outcome.previousEmail : undefined,
      });
      onOpenChange(false);
    } catch (err) {
      if (runRef.current !== run) return;
      const code = err instanceof PlatformApiError ? err.code : null;
      const msg = err instanceof Error ? err.message : "The change failed.";
      setError({ text: msg, code });
    } finally {
      if (runRef.current === run) setBusy(false);
    }
  }, [api, orgId, user, gate, mode, reason, onChanged, onOpenChange]);

  if (!user) return null;

  const advice = error ? adviseOnFailure(error.code) : null;
  const critical = advice?.severity === "critical";
  // An unavailable server says nothing about this user or this address, so it
  // must not be dressed as a failed change — amber, not red.
  const unavailable = advice?.severity === "unavailable";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl space-y-4" data-testid="change-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg text-foreground">
            <AtSign className="h-4 w-4" />
            Change sign-in email
          </DialogTitle>
          <DialogDescription>
            Moves this user&apos;s Clerk sign-in identifier and the Lexiun record
            together. For a user who has <strong>already accepted</strong> —
            correcting a still-pending invitation is done from Edit instead.
          </DialogDescription>
        </DialogHeader>

        {/* Who — the target, stated plainly. The confirm field guards the
            destination, so identity has to be legible rather than typed. */}
        <div className="rounded-md border border-border bg-panel-2 p-3 text-sm">
          <div className="font-medium text-foreground">
            {user.displayName || "(no display name)"}
          </div>
          <div className="font-mono text-xs text-muted">{user.email}</div>
          <div className="font-mono text-[11px] text-faint">{user.userId}</div>
        </div>

        <div className="space-y-1">
          <label htmlFor="change-email-new" className="text-xs font-medium text-muted">
            New email address:
          </label>
          <input
            id="change-email-new"
            type="email"
            autoComplete="off"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            disabled={busy}
            data-testid="change-email-new"
            className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:opacity-60"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="change-email-confirm" className="text-xs font-medium text-muted">
            Retype the new address — it becomes a working login immediately:
          </label>
          <input
            id="change-email-confirm"
            type="email"
            autoComplete="off"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            disabled={busy}
            data-testid="change-email-confirm"
            className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:opacity-60"
          />
        </div>

        {/* D-1 — required, no default. Rendered as an explicit choice so the
            permissive mode can never be reached by leaving a control alone. */}
        <fieldset className="space-y-1.5">
          <legend className="text-xs font-medium text-muted">How is the new address verified?</legend>
          {(["admin-verified", "user-verified"] as OwnershipMode[]).map((m) => {
            const enabled = isModeImplemented(m);
            return (
              <label
                key={m}
                className={`flex items-start gap-2 text-xs ${enabled ? "text-foreground" : "text-faint"}`}
              >
                <input
                  type="radio"
                  name="ownershipMode"
                  value={m}
                  checked={mode === m}
                  disabled={!enabled || busy}
                  onChange={() => setMode(m)}
                  data-testid={`change-email-mode-${m}`}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium">
                    {m === "admin-verified" ? "Admin-verified" : "User-verified"}
                  </span>
                  {m === "admin-verified" ? (
                    <span className="block text-muted">
                      Marked verified without proof of ownership. Works immediately —
                      and works for whoever owns the address you type.
                    </span>
                  ) : (
                    <span className="block">
                      The user proves they own it before it takes effect.{" "}
                      <strong>Not available yet</strong> — server returns 501.
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </fieldset>

        <div className="space-y-1">
          <label htmlFor="change-email-reason" className="text-xs font-medium text-muted">
            Reason (audited, optional; max {REASON_MAX}):
          </label>
          <textarea
            id="change-email-reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            data-testid="change-email-reason"
            className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:opacity-60"
          />
        </div>

        {/* What actually happens — traced/verified, not assumed. The session
            fact is the one that surprises people, so it leads. */}
        <div className="rounded-md border border-border bg-panel-2 p-3 text-xs text-muted">
          <p className="mb-1 font-medium text-foreground">What this does:</p>
          <ul className="list-disc space-y-0.5 pl-4">
            <li>
              Their <strong>password is unchanged</strong> — they sign in with the new
              address and the same password. No reset, no re-invite.
            </li>
            <li>
              Any <strong>session they have open right now keeps working</strong> — Clerk
              does not sign them out, and nothing here forces it. Tell them, or they
              will not notice until next sign-in.
            </li>
            <li>
              The old address is <strong>removed</strong> from their login and becomes
              free to invite again. Keeping it would leave two working logins.
            </li>
          </ul>
        </div>

        {error && (
          <div
            className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
              unavailable
                ? "border-warning/40 bg-warning/10 text-warning"
                : critical
                  ? "border-critical bg-critical/15 text-critical"
                  : "border-critical/40 bg-critical/10 text-critical"
            }`}
            data-testid="change-email-error"
            data-severity={advice?.severity ?? "terminal"}
          >
            {critical ? (
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <div className="space-y-1">
              {advice && <p className="font-semibold">{advice.headline}</p>}
              <p>{error.text}</p>
              {advice && !advice.retryable && !unavailable && (
                <p className="text-xs opacity-90">
                  Retrying will not help. Reload and check the current state before acting.
                </p>
              )}
              {unavailable && (
                <p className="text-xs opacity-90">
                  Nothing was changed, and this is not about this user or address.
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="change-email-cancel"
            className="rounded-md border border-border bg-panel-2 px-3 py-1.5 text-sm font-medium text-muted transition-colors hover:bg-accent hover:text-foreground disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!gate.canSubmit}
            title={gate.canSubmit ? undefined : gate.reason}
            onClick={() => void handleSubmit()}
            data-testid="change-email-submit"
            className="inline-flex items-center gap-1 rounded-md bg-critical px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-critical/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {busy ? "Changing…" : "Change sign-in email"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
